<template>
  <div id="app">
    <div v-if="!isProduction" class="env-banner">
      ⚠️ {{ envLabel }} — {{ hostname }}
    </div>
    <router-view />
    <GlobalSpinner />
    <FeedbackWidget />
    <footer class="app-footer">
      © {{ currentYear }} <a href="https://www.incentit.com" target="_blank" rel="noopener noreferrer">Incentit</a> | Version {{ appVersion }}
    </footer>
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed } from 'vue'
import { logger } from '@/services/logger'
import GlobalSpinner from '@/components/GlobalSpinner.vue'
import FeedbackWidget from '@/components/FeedbackWidget.vue'
import { useWebAnalytics } from '@/composables/useWebAnalytics'
import { isProductionRuntime } from '@/utils/runtimeAccess'

const { initializeAnalytics, setupAnalyticsWatcher } = useWebAnalytics()

const isProduction = isProductionRuntime
const hostname = window.location.hostname
const envLabel = computed(() => hostname.includes('qa') ? 'QA ENVIRONMENT' : 'DEV / NON-PRODUCTION')
const currentYear = new Date().getFullYear()
const appVersion = __APP_VERSION__

onMounted(() => {
  logger.info('Tenant Reporting Engine started', {
    environment: import.meta.env.MODE,
    timestamp: new Date().toISOString()
  })
  
  // Initialize web analytics tracking
  initializeAnalytics()
  setupAnalyticsWatcher()
})
</script>

<style>
.env-banner {
  background: #dc2626;
  color: #fff;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 3px 0;
  letter-spacing: 0.5px;
}

@media print {
  .env-banner {
    display: none !important;
  }
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  height: 100%;
  width: 100%;
  overflow: hidden;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  background: #f8fafc;
  color: #2d3748;
  line-height: 1.6;
}

#app {
  height: 100vh;
  width: 100vw;
}

/* Global utility classes */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Scrollbar styling */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Focus styles for accessibility */
button:focus,
input:focus,
select:focus,
textarea:focus {
  outline: 2px solid #667eea;
  outline-offset: 2px;
}

/* Smooth transitions */
* {
  transition: color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;
}
</style>


<style scoped>
.app-footer {
  position: fixed;
  bottom: 0;
  right: 0;
  padding: 4px 12px;
  font-size: 0.75rem;
  color: #888;
  background: rgba(255, 255, 255, 0.9);
  border-top-left-radius: 4px;
  z-index: 100;
}

.app-footer a {
  color: #667eea;
  text-decoration: none;
}

.app-footer a:hover {
  text-decoration: underline;
}

@media print {
  .app-footer {
    display: none;
  }
}
</style>
